import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { API_BASE_URL } from './api-url.token';

@Injectable({
  providedIn: 'root'
})
/**
 * Provides API access for the Spend Intelligence dashboard including category spend breakdowns,
 * tail-spend analysis, drill-through queries, and recommendation data.
 */
export class SpendIntelligenceService {

  private readonly apiURL = inject(API_BASE_URL) + 'SpendIntelligence';

  HTTP_HEADER = new HttpHeaders({
    'Content-type': 'application/json'
  });

  httpHeaderOptions = { headers: this.HTTP_HEADER };

  private readonly http = inject(HttpClient);

  constructor() { }


  /**
   * Fetches total spend aggregated by category.
   * @param data The request payload with organisation and period filters.
   * @returns An Observable of the total category spend data.
   */
  getTotalCategorySpend(data: any = {}) {
    return this.http.post(this.apiURL + '/getTotalCategorySpend', data, this.httpHeaderOptions);
  }

  /**
   * Fetches the tail-spend supplier list for the intelligence dashboard.
   * @param data The request payload with organisation and period filters.
   * @returns An Observable of the tail-spend suppliers data.
   */
  getTailSpendSuppliers(data: any = {}) {
    return this.http.post(this.apiURL + '/getTailSpendSuppliers', data, this.httpHeaderOptions);
  }

  /**
   * Fetches spend broken down by Level 1 category for chart rendering.
   * @param data The request payload with organisation and period filters.
   * @returns An Observable of the Level 1 category breakdown data.
   */
  getSpendByLevel1CategoryBreakdown(data: any = {}) {
    return this.http.post(this.apiURL + '/getSpendByLevel1CategoryBreakdown', data, this.httpHeaderOptions);
  }

  /**
   * Fetches the top 5 Level 4 spend categories.
   * @param data The request payload with organisation and period filters.
   * @returns An Observable of the top 5 Level 4 spend category data.
   */
  getTop5Level4SpendCategory(data: any = {}) {
    return this.http.post(this.apiURL + '/getTop5Level4SpendCategory', data, this.httpHeaderOptions);
  }

  /**
   * Fetches yearly spend broken down by category for trend analysis.
   * @param data The request payload with organisation and period filters.
   * @returns An Observable of the yearly category spend data.
   */
  getYearlySpendCategory(data: any = {}) {
    return this.http.post(this.apiURL + '/getYearlySpendCategory', data, this.httpHeaderOptions);
  }

  /**
   * Fetches spend recommendation categories for the intelligence panel.
   * @param data The request payload with organisation and period filters.
   * @returns An Observable of the spend recommendations data.
   */
  getSpendRecommendationsCategory(data: any = {}) {
    return this.http.post(this.apiURL + '/getSpendRecommendationsCategory', data, this.httpHeaderOptions);
  }

  /**
   * Fetches tail-spend distribution grouped by category.
   * @param data The request payload with organisation and period filters.
   * @returns An Observable of the tail-spend distribution data.
   */
  getTailSpendDistributionByCategory(data: any = {}) {
    return this.http.post(this.apiURL + '/getTailSpendDistributionByCategory', data, this.httpHeaderOptions);
  }

  /**
   * Fetches the head-of-tail spend summary data.
   * @param data The request payload with organisation and period filters.
   * @returns An Observable of the head-of-tail spend data.
   */
  getHeadOfTailSpend(data: any = {}) {
    return this.http.post(this.apiURL + '/getHeadOfTailSpend', data, this.httpHeaderOptions);
  }

  /**
   * Fetches the tail-of-tail spend summary data.
   * @param data The request payload with organisation and period filters.
   * @returns An Observable of the tail-of-tail spend data.
   */
  getTailOfTailSpend(data: any = {}) {
    return this.http.post(this.apiURL + '/getTailOfTailSpend', data, this.httpHeaderOptions);
  }

  /**
   * Fetches drill-through spend data by Level 4 taxonomy node.
   * @param data The request payload identifying the taxonomy node and filters.
   * @returns An Observable of the drill-through data.
   */
  getSpendIntelligenceDrillByLevel4Taxonomy(data: any = {}) {
    return this.http.post(this.apiURL + '/getSpendIntelligenceDrillByLevel4Taxonomy', data, this.httpHeaderOptions);
  }

  /**
   * Fetches drill-through spend data by Level 1 category.
   * @param data The request payload identifying the category and filters.
   * @returns An Observable of the drill-through data.
   */
  getSpendIntelligenceDrillByLevel1Category(data: any = {}) {
    return this.http.post(this.apiURL + '/getSpendIntelligenceDrillByLevel1Category', data, this.httpHeaderOptions);
  }

  /**
   * Fetches drill-through spend data broken down by year.
   * @param data The request payload with organisation and category filters.
   * @returns An Observable of the yearly drill-through data.
   */
  getSpendIntelligenceDrillByYear(data: any = {}) {
    return this.http.post(this.apiURL + '/getSpendIntelligenceDrillByYear', data, this.httpHeaderOptions);
  }

  /**
   * Fetches drill-through spend data filtered by recommendation category.
   * @param data The request payload identifying the recommendation category.
   * @returns An Observable of the recommendation drill-through data.
   */
  getSpendIntelligenceDrillByRecommendationCategory(data: any = {}) {
    return this.http.post(this.apiURL + '/getSpendIntelligenceDrillByRecommendationCategory', data, this.httpHeaderOptions);
  }

  /**
   * Fetches drill-through spend data for tail-spend at Level 1.
   * @param data The request payload with organisation and period filters.
   * @returns An Observable of the tail-spend Level 1 drill-through data.
   */
  getSpendIntelligenceDrillByTailSpendLevel1(data: any = {}) {
    return this.http.post(this.apiURL + '/getSpendIntelligenceDrillByTailSpendLevel1', data, this.httpHeaderOptions);
  }

  /**
   * Fetches drill-through spend data for head-of-tail spend grouped by supplier.
   * @param data The request payload identifying the supplier and filters.
   * @returns An Observable of the head-of-tail supplier drill-through data.
   */
  getSpendIntelligenceDrillByHeadOfTailSupplier(data: any = {}) {
    return this.http.post(this.apiURL + '/getSpendIntelligenceDrillByHeadOfTailSupplier', data, this.httpHeaderOptions);
  }

  /**
   * Fetches drill-through spend data applying multiple selected filters simultaneously.
   * @param data The request payload with multi-select filter criteria.
   * @returns An Observable of the multi-select drill-through data.
   */
  getSpendIntelligenceDrillByMultiSelect(data: any = {}) {
    return this.http.post(this.apiURL + '/getSpendIntelligenceDrillByMultiSelect', data, this.httpHeaderOptions);
  }
}


